import React, { useEffect, useRef } from 'react';

export default function LegalHero({ title, eyebrow, supportingText, effectiveDate, lastUpdated }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let nodes = [], animId;

    function resize() {
      if(canvas.parentElement) {
        canvas.width = canvas.parentElement.offsetWidth;
        canvas.height = canvas.parentElement.offsetHeight;
      }
    }

    function initNodes() {
      nodes = [];
      const count = Math.floor((canvas.width * canvas.height) / 18000); // slightly less dense
      for (let i = 0; i < count; i++) {
        nodes.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.2,
          vy: (Math.random() - 0.5) * 0.2,
          r: Math.random() * 1.5 + 0.5
        });
      }
    }

    function draw() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx*dx + dy*dy);
          if (dist < 100) {
            const alpha = (1 - dist/100) * 0.12;
            ctx.beginPath();
            ctx.strokeStyle = `rgba(254,171,61,${alpha})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
      }

      ctx.save();
      for (let i = 0; i < 4; i++) {
        const x1 = (i / 3) * canvas.width;
        const cp1x = x1 + 80;
        const cp1y = canvas.height * 0.3;
        const cp2x = x1 + 40;
        const cp2y = canvas.height * 0.7;
        ctx.beginPath();
        ctx.strokeStyle = `rgba(12,91,148,0.08)`;
        ctx.lineWidth = 1;
        ctx.moveTo(x1, 0);
        ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, x1 + 20, canvas.height);
        ctx.stroke();
      }
      ctx.restore();

      for (const n of nodes) {
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(254,171,61,0.3)';
        ctx.fill();

        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > canvas.width) n.vx *= -1;
        if (n.y < 0 || n.y > canvas.height) n.vy *= -1;
      }

      animId = requestAnimationFrame(draw);
    }

    window.addEventListener('resize', () => { resize(); initNodes(); });
    resize();
    initNodes();
    draw();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animId);
    };
  }, []);

  return (
    <section className="relative overflow-hidden w-full flex flex-col justify-center items-center text-center px-6" style={{ minHeight: '380px', paddingTop: '100px', paddingBottom: '40px', background: 'linear-gradient(180deg, #05192E 0%, #06203A 100%)' }}>
      {/* SVG Grid Background */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none z-0" style={{ filter: 'blur(1px)', opacity: 0.8 }} viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice">
        <rect width="1440" height="900" fill="#04152a"/>
        <g stroke="rgba(79,144,166,0.08)" strokeWidth="1">
          <line x1="0" y1="150" x2="1440" y2="150"/>
          <line x1="0" y1="300" x2="1440" y2="300"/>
          <line x1="0" y1="450" x2="1440" y2="450"/>
          <line x1="0" y1="600" x2="1440" y2="600"/>
          <line x1="0" y1="750" x2="1440" y2="750"/>
          <line x1="180" y1="0" x2="180" y2="900"/>
          <line x1="360" y1="0" x2="360" y2="900"/>
          <line x1="540" y1="0" x2="540" y2="900"/>
          <line x1="720" y1="0" x2="720" y2="900"/>
          <line x1="900" y1="0" x2="900" y2="900"/>
          <line x1="1080" y1="0" x2="1080" y2="900"/>
          <line x1="1260" y1="0" x2="1260" y2="900"/>
        </g>
      </svg>

      {/* Radial Glow */}
      <div className="absolute inset-0 z-1 pointer-events-none" style={{ background: 'radial-gradient(circle at 50% 50%, rgba(12,91,148,0.15) 0%, transparent 60%)' }}></div>

      {/* Overlays to ensure text contrast */}
      <div className="absolute inset-0 z-1 pointer-events-none bg-[#05192E]/40"></div>

      {/* Canvas for connecting lines and nodes */}
      <canvas className="absolute inset-0 pointer-events-none z-2" ref={canvasRef}></canvas>

      {/* Content */}
      <div className="relative z-10 max-w-3xl mx-auto w-full flex flex-col items-center">
        <div className="inline-block text-[#FEAB3D] font-bold tracking-[0.15em] uppercase text-xs mb-5 px-3 py-1 bg-[#FEAB3D]/10 rounded-full border border-[#FEAB3D]/20 backdrop-blur-sm">
          {eyebrow}
        </div>
        <h1 className="font-['Fraunces',serif] text-4xl sm:text-5xl lg:text-[54px] font-bold mb-4 tracking-tight text-white leading-tight">
          {title}
        </h1>
        <div className="text-white/80 text-lg sm:text-xl font-medium mb-8">
          {supportingText}
        </div>
        <div className="flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-6 text-sm">
          <div className="text-white/65">
            Effective date: <span className="font-semibold text-white">{effectiveDate}</span>
          </div>
          <div className="hidden sm:block w-1 h-1 bg-white/20 rounded-full"></div>
          <div className="text-white/65">
            Last updated: <span className="font-semibold text-white">{lastUpdated}</span>
          </div>
        </div>
      </div>
    </section>
  );
}
